import IncidentReport from '../domain/models/IncidentReport';

const toParticipant = (entry) => ({
  id: entry.id,
  type: entry.type,
  name: entry.name || '',
  role: entry.role || ''
});

export default class IncidentService {
  constructor(repository, studentService, inventoryService, auditLogger) {
    this.repository = repository;
    this.studentService = studentService;
    this.inventoryService = inventoryService;
    this.auditLogger = auditLogger;
  }

  list() {
    return this.repository.getAll().map((incident) => new IncidentReport(incident));
  }

  getById(id) {
    const raw = this.repository.getById(id);
    return raw ? new IncidentReport(raw) : null;
  }

  create(data, user) {
    const participants = (data.participants || []).map(toParticipant);
    const incident = new IncidentReport({
      ...data,
      participants,
      reportedBy: data.reportedBy || user?.name || 'System',
      createdBy: user?.name || 'System',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    this.repository.create(incident.toJSON());

    const studentIds = participants.filter((p) => p.type === 'student').map((p) => p.id);
    studentIds.forEach((studentId) => {
      if (this.studentService) {
        this.studentService.update(studentId, { lastUpdated: new Date().toISOString() }, user);
      }
    });

    this.auditLogger?.log('incident', 'Incident created', { incidentId: incident.id }, user);
    return incident;
  }

  update(id, data, user) {
    const existing = this.getById(id);
    if (!existing) return null;
    const updated = new IncidentReport({
      ...existing.toJSON(),
      ...data,
      id,
      updatedAt: new Date().toISOString()
    });
    this.repository.update(id, updated.toJSON());
    this.auditLogger?.log('incident', 'Incident updated', { incidentId: id, changes: data }, user);
    return updated;
  }

  close(id, user) {
    return this.update(id, { status: 'closed' }, user);
  }

  listByStudent(studentId) {
    return this.list().filter((incident) =>
      incident.participants.some((p) => p.type === 'student' && p.id === studentId)
    );
  }

  listByInventoryItem(itemId) {
    return this.list().filter((incident) => incident.inventoryItemIds.includes(itemId));
  }

  seedFromLegacyReports(students, user) {
    const existing = this.repository.getAll();
    if (existing.length > 0) return;

    const inventoryLookup = this.inventoryService
      ? this.inventoryService.list().reduce((acc, item) => {
          acc[item.name] = item.id;
          return acc;
        }, {})
      : {};

    const incidents = [];
    students.forEach((student) => {
      (student.incidentReports || []).forEach((report) => {
        if (!report) return;
        const legacyInventory = report.inventoryItems || [];
        const inventoryItemIds = legacyInventory.map((name) => inventoryLookup[name] || name);
        incidents.push({
          id: report.id || `inc-${Math.random().toString(36).slice(2, 9)}`,
          type: report.type || 'Other',
          date: report.date || new Date().toISOString(),
          reportedBy: report.createdBy || 'System',
          description: report.description || report.title || '',
          severity: report.severity || 'medium',
          status: report.status || 'open',
          assignedKeyWorker: report.assignedTo || '',
          followUpRequired: Boolean(report.followUpDate),
          followUpDate: report.followUpDate || null,
          followUpPrompt: report.followUpPrompt || '',
          participants: [
            { id: student.id, type: 'student', name: student.name, role: 'Student' }
          ],
          inventoryItemIds,
          documents: (report.attachments || []).map((name) => ({ name }))
        });
      });
    });

    incidents.forEach((incident) => {
      this.repository.create(new IncidentReport(incident).toJSON());
    });

    if (incidents.length > 0) {
      this.auditLogger?.log('incident', 'Legacy incidents imported', { count: incidents.length }, user);
    }
  }
}
